import { useEffect, useState } from "react";
import { Headphones, TrendingUp, Globe2 } from "lucide-react";
import { api, fileUrl } from "@/lib/api";

const REGION = new Intl.DisplayNames(["it"], { type: "region" });
const countryName = (cc) => { if (cc === "ZZ") return "Sconosciuto"; try { return REGION.of(cc); } catch { return cc; } };

const CountryChip = ({ c }) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-white/5 border border-white/10 px-2 py-0.5 text-[11px] text-zinc-300">
    <b className="font-mono-tga text-emerald-300">{c.country}</b> {c.plays}
  </span>
);

const Row = ({ t, i, max }) => (
  <div className="flex items-center gap-4 rounded-xl border border-white/10 bg-[#1e2236] p-3" data-testid={`top-track-${t.track_id}`}>
    <span className={`font-display text-2xl font-bold w-9 text-center ${i < 3 && t.plays ? "text-emerald-300" : "text-zinc-500"}`}>{i + 1}</span>
    {t.cover_url ? <img src={fileUrl(t.cover_url)} alt="" className="h-12 w-12 rounded-lg object-cover" /> : <div className="h-12 w-12 rounded-lg bg-white/5" />}
    <div className="flex-1 min-w-0">
      <p className="font-semibold truncate">{t.title}</p>
      <p className="text-xs text-zinc-400 truncate">{t.artist_name} · {t.release_title}{t.genre ? ` · ${t.genre}` : ""}</p>
      <div className="mt-2 h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full bg-emerald-400 transition-[width] duration-700" style={{ width: `${max ? (t.plays / max) * 100 : 0}%` }} /></div>
      {!!t.countries?.length && <div className="mt-2 flex flex-wrap gap-1" data-testid={`top-track-countries-${t.track_id}`}>{t.countries.map((c) => <CountryChip key={c.country} c={c} />)}</div>}
    </div>
    <div className="text-right shrink-0">
      <p className="font-bold text-lg" data-testid={`top-track-plays-${t.track_id}`}>{t.plays}</p>
      <p className="text-[11px] text-zinc-400">ascolti · <span className="text-emerald-300">{t.plays_7d}</span> in 7 gg</p>
    </div>
  </div>
);

const Countries = ({ list }) => {
  const total = list.reduce((a, c) => a + c.plays, 0);
  return (
    <div className="rounded-xl border border-white/10 bg-[#1e2236] p-5 mb-8" data-testid="top-countries">
      <p className="font-display font-bold uppercase mb-4 inline-flex items-center gap-2"><Globe2 className="h-4 w-4 text-emerald-300" /> Ascolti per Paese</p>
      {!list.length ? <p className="text-sm text-zinc-400" data-testid="top-countries-empty">Ancora nessun ascolto registrato.</p> : (
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
          {list.slice(0, 12).map((c) => (
            <div key={c.country} data-testid={`top-country-${c.country}`}>
              <div className="flex justify-between text-sm"><span>{countryName(c.country)}</span><span className="text-zinc-400">{c.plays} · {Math.round((c.plays / total) * 100)}%</span></div>
              <div className="mt-1 h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full bg-hot transition-[width] duration-700" style={{ width: `${(c.plays / list[0].plays) * 100}%` }} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const AdminTopTracks = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/plays").then((r) => setData(r.data)); }, []);
  if (!data) return null;
  const list = data.tracks;
  const max = Math.max(0, ...list.map((t) => t.plays));
  const total = list.reduce((a, t) => a + t.plays, 0);
  const week = list.reduce((a, t) => a + t.plays_7d, 0);
  return (
    <div data-testid="admin-top-tracks">
      <h2 className="font-display text-2xl font-bold uppercase tracking-tight mb-2">Classifica brani</h2>
      <p className="text-sm text-zinc-400 mb-6 flex flex-wrap gap-x-5 gap-y-1">
        <span className="inline-flex items-center gap-1"><Headphones className="h-4 w-4" /> Ascolti totali: <b className="text-white" data-testid="top-tracks-total">{total}</b></span>
        <span className="inline-flex items-center gap-1"><TrendingUp className="h-4 w-4" /> Ultimi 7 giorni: <b className="text-emerald-300">{week}</b></span>
        <span>Conta 1 ascolto per visitatore ogni 30 minuti; i tuoi ascolti da admin non vengono contati.</span>
      </p>
      <Countries list={data.countries} />
      {!list.length && <p className="text-sm text-zinc-400 border border-dashed border-white/15 p-10 text-center" data-testid="top-tracks-empty">Pubblica un'uscita con brani per vedere la classifica.</p>}
      <div className="space-y-2">{list.map((t, i) => <Row key={t.track_id} t={t} i={i} max={max} />)}</div>
    </div>
  );
};
